import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  SHAREABLE_TYPE_DISPLAY_NAME,
  BdResource,
  ShareableType,
  BdResources,
} from '@bindoc-projects/resource-domain-lib';
import {
  BdResourceAbstractSelectInline,
  BdResourceSelectInlineOption,
} from '../abstract-inline-select';
import {
  BD_RESOURCES_SELECT_INLINE_CONFIG_DEFAULT,
  BdResourcesSelectInlineConfig,
} from '../inline-options';
import {
  MultiSelectChangeEvent,
  MultiSelectModule,
  MultiSelectSelectAllChangeEvent,
} from 'primeng/multiselect';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BD_RESOURCES_DEFAULT_SORTING } from '../../../core/sorting.const';
import { BdResourceServices } from '../../../resources/service/resource.service';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { firstValueFrom, Observable, of } from 'rxjs';
import { BdDataTestDirective } from '@bindoc-projects/util-lib/ui';
import { IftaLabel } from 'primeng/iftalabel';
import { OverlayOptions } from 'primeng/api';

@Component({
  selector: 'bd-resources-select-inline-multiple',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-iftalabel>
      <!-- PrimeNG 22 treats an unset selectAll as "not all selected", so toggle-all could never deselect -->
      <p-multiselect
        [selectAll]="null"
        [id]="
          'bd-resource-select-inline-multiple-' +
          resolvedConfig.resourceNameTitle
        "
        [data-test]="
          'bd-resource-select-inline-multiple-' +
          resolvedConfig.resourceNameTitle
        "
        [options]="resolvedResourceOptions"
        optionDisabled="disabled"
        optionLabel="resource.name"
        dataKey="id"
        [class]="
          optionsLoadFailed
            ? 'w-full ng-invalid ng-dirty'
            : unavailableHint
              ? 'w-full'
              : 'w-full mb-4'
        "
        [inputId]="
          'bd-resource-select-inline-multiple-' +
          resolvedConfig.resourceNameTitle
        "
        [formControl]="resourceCtrl"
        [showToggleAll]="showHeader"
        [showHeader]="showHeader"
        [showClear]="true"
        [loading]="formLoading"
        [filter]="showHeader"
        [placeholder]="resolvedConfig.resourceNameTitle"
        [filterPlaceHolder]="resolvedConfig.resourceNameTitle"
        [emptyFilterMessage]="resolvedConfig.emptyResourcesText"
        [maxSelectedLabels]="3"
        [selectedItemsLabel]="selectedItemsLabel"
        variant="filled"
        [overlayOptions]="overlayOptions"
        (onChange)="onChange($event)"
        (onSelectAllChange)="onSelectAllChange($event)"
        (onClear)="onClearChange()"
      >
        <ng-template let-option #item>
          <div class="flex align-items-center gap-2">
            <span [class]="option.styleClass">{{ option.resource.name }}</span>
          </div>
        </ng-template>
      </p-multiselect>
      <label
        [for]="
          'bd-resource-select-inline-multiple-' +
          resolvedConfig.resourceNameTitle
        "
        >{{ resolvedConfig.resourceNameTitle }}</label
      >
      @if (optionsLoadFailed) {
        <small class="bd-resource-select-error">
          <i class="pi pi-exclamation-triangle"></i>
          Laden fehlgeschlagen
        </small>
      } @else if (unavailableHint) {
        <small class="bd-resource-select-hint">
          <i class="pi pi-info-circle"></i>
          {{ unavailableHint }}
        </small>
      }
    </p-iftalabel>
  `,
  styles: [
    '.bd-resource-select-error, .bd-resource-select-hint { display: inline-flex; align-items: center; gap: 0.25rem; font-size: 0.75rem; }',
    '.bd-resource-select-error { color: var(--p-form-field-invalid-placeholder-color); }',
    '.bd-resource-select-hint { color: var(--p-text-muted-color); }',
  ],
  imports: [
    MultiSelectModule,
    FormsModule,
    ReactiveFormsModule,
    BdDataTestDirective,
    IftaLabel,
  ],
})
export class BdResourcesSelectInlineMultipleComponent<
  TResource extends BdResource,
> extends BdResourceAbstractSelectInline<
  TResource,
  BdResourceSelectInlineOption<TResource>[]
> {
  public readonly resourceOptions = input<
    BdResourceSelectInlineOption<TResource>[] | undefined
  >(undefined);
  public readonly config = input<BdResourcesSelectInlineConfig<TResource>>(
    BD_RESOURCES_SELECT_INLINE_CONFIG_DEFAULT,
  );

  readonly resourceSelects = input<BdResourceSelectInlineOption<TResource>[]>(
    [],
  );
  readonly resourceName = input<ShareableType>();

  public readonly resourceSelectChange =
    output<BdResourceSelectInlineOption<TResource>[]>();

  public readonly overlayOptions: OverlayOptions = {
    mode: 'overlay',
    appendTo: 'body',
  };

  public selectedItemsLabel = `{0} ausgewählt`;
  public showHeader = false;
  public unavailableHint: string | null = null;
  private static readonly HEADER_THRESHOLD = 3;

  private readonly _cdr = inject(ChangeDetectorRef);

  constructor() {
    const resourceServices = inject(BdResourceServices);

    super(resourceServices);

    effect(() => {
      this.resolvedConfig = this.config();
    });

    effect(() => {
      const options = this.resourceOptions();
      if (options !== undefined) {
        this.resolvedResourceOptions = options;
        // Same reason as in the single select: parent-supplied options land
        // after the first binding, so the header/filter threshold has to be
        // recomputed when they do.
        this._resourceOptionsLoaded$.next(this.resolvedResourceOptions);
      }
    });

    this.resourceCtrl.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((resources) => {
        this._onSelect(resources);
      });

    this._resourceOptionsLoaded$
      .asObservable()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((options) => {
        this.showHeader =
          options.length >
          BdResourcesSelectInlineMultipleComponent.HEADER_THRESHOLD;
        this._cdr.markForCheck();
      });

    effect(() => {
      const name = this.resourceName();
      if (name) {
        this._onResourceNameChange(name);
        this.selectedItemsLabel = `{0} ${SHAREABLE_TYPE_DISPLAY_NAME[name]} ausgewählt`;
      }
    });

    effect(() => {
      const selects = this.resourceSelects();
      if (selects) {
        this._onResourceSelectChange(selects);
      }
    });
  }

  public onChange(change: MultiSelectChangeEvent): void {
    this._onSelect(change.value);
  }

  public onSelectAllChange(change: MultiSelectSelectAllChangeEvent): void {
    this._onSelect(change.checked ? this.resourceSelects() : []);
  }

  public onClearChange(): void {
    this._onSelect([]);
  }

  protected _onSelect(
    resourceSelects: BdResourceSelectInlineOption<TResource>[],
  ): void {
    this.resourceSelectChange.emit(resourceSelects);
  }

  private _onResourceSelectChange(
    resourceSelects: BdResourceSelectInlineOption<TResource>[],
  ): void {
    this.updateResourceSelectResolvingInProgress(true);
    const resourceSelectsReq: Array<
      Promise<BdResourceSelectInlineOption<TResource>>
    > = [];

    for (const resourceSelect of resourceSelects) {
      if (resourceSelect.resource) {
        resourceSelectsReq.push(Promise.resolve(resourceSelect));
      } else {
        const req: Observable<BdResourceSelectInlineOption<TResource>> =
          this._resourceOptionsLoaded$.pipe(
            switchMap((options) => {
              if (options.some((option) => option.id === resourceSelect.id)) {
                return of(
                  options.find((option) => option.id === resourceSelect.id),
                );
              } else {
                return this._resourceService.findById(resourceSelect.id).pipe(
                  take(1),
                  map((resource) =>
                    this.resourceAsAdminInlineOption(resource as TResource),
                  ),
                  tap((option) => this.addOption(option)),
                  catchError((err) => {
                    const placeholder = this.resolveExpectedErrorPlaceholder(
                      err,
                      resourceSelect.id,
                    );
                    if (placeholder) {
                      this.addOption(placeholder);
                      return of(placeholder);
                    }
                    throw err;
                  }),
                );
              }
            }),
          );

        resourceSelectsReq.push(firstValueFrom(req));
      }
    }

    Promise.all(resourceSelectsReq)
      .then((resolvedSelects) => {
        this.updateResourceSelectResolvingInProgress(false);
        this.unavailableHint =
          this.buildUnavailableSelectionHint(resolvedSelects);

        this.resourceCtrl.setValue(
          resolvedSelects.sort((a, b) =>
            BdResources.sort(
              a.resource,
              b.resource,
              BD_RESOURCES_DEFAULT_SORTING,
            ),
          ),
          { emitEvent: false },
        );
        this._cdr.markForCheck();
      })
      .catch(() => {
        this.updateResourceSelectResolvingInProgress(false);
        this.optionsLoadFailed = true;
        this._cdr.markForCheck();
      });
  }
}
